import { emptyValueForSchema, getSchemaAt, CASE_TYPE_SCHEMA } from "../../../../case-spec";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  ArrayShell,
  BoolField,
  EnumSelect,
  MediaUrlField,
  RowCard,
  StringListField,
  TextField,
  useOpenIndex,
} from "../shared-ui";
import {
  asArray,
  bool,
  enumOptionsAt,
  isRecord,
  num,
  str,
  type PanelProps,
} from "../shared";
import {
  appendAtPath,
  childPath,
  getAtPath,
  setAtPath,
} from "../path-utils";

/** Tinder: profiles + matches tabs */
export function TinderPanel({ root, disabled, onChangeRoot }: PanelProps) {
  const [tab, setTab] = useState<"profiles" | "matches">("profiles");
  const path =
    tab === "profiles"
      ? "device.apps.tinder.profiles"
      : "device.apps.tinder.matches";
  const items = asArray(getAtPath(root, path));
  const { openIndex, setOpenIndex, toggle } = useOpenIndex(items.length);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-1">
        {(
          [
            ["profiles", "资料", "device.apps.tinder.profiles"],
            ["matches", "匹配", "device.apps.tinder.matches"],
          ] as const
        ).map(([key, label, p]) => (
          <Button
            key={key}
            type="button"
            size="sm"
            variant={tab === key ? "secondary" : "ghost"}
            onClick={() => {
              setTab(key);
              setOpenIndex(null);
            }}
          >
            {label} ({asArray(getAtPath(root, p)).length})
          </Button>
        ))}
      </div>
      <ArrayShell
        title={tab === "profiles" ? "资料" : "匹配"}
        count={items.length}
        disabled={disabled}
        onAdd={() => {
          const schema = getSchemaAt(CASE_TYPE_SCHEMA, `${path}[0]`);
          const row = schema
            ? emptyValueForSchema(schema)
            : tab === "profiles"
              ? {
                  name: "",
                  age: 0,
                  distance: 0,
                  bio: "",
                  tags: [],
                  verified: false,
                  image_url: "",
                }
              : {
                  profile: "",
                  message: "",
                  unread: false,
                  last_message_at: "",
                };
          onChangeRoot(appendAtPath(root, path, row));
          setOpenIndex(items.length);
        }}
      >
        <ul className="flex flex-col gap-2">
          {items.map((item, index) => {
            const rec = isRecord(item) ? item : {};
            const itemPath = childPath(path, index);
            const title =
              tab === "profiles"
                ? str(rec.name) || `资料 ${index + 1}`
                : `${str(rec.profile)} · ${str(rec.message).slice(0, 24)}`;
            return (
              <RowCard
                key={itemPath}
                title={title}
                open={openIndex === index}
                onToggle={() => toggle(index)}
                disabled={disabled}
                index={index}
                total={items.length}
                arrayPath={path}
                root={root}
                onChangeRoot={onChangeRoot}
              >
                {tab === "profiles" ? (
                  <>
                    <TextField
                      label="姓名"
                      value={str(rec.name)}
                      disabled={disabled}
                      onChange={(v) =>
                        onChangeRoot(
                          setAtPath(root, childPath(itemPath, "name"), v),
                        )
                      }
                    />
                    <TextField
                      label="年龄"
                      value={String(num(rec.age))}
                      disabled={disabled}
                      onChange={(v) =>
                        onChangeRoot(
                          setAtPath(
                            root,
                            childPath(itemPath, "age"),
                            Number(v) || 0,
                          ),
                        )
                      }
                    />
                    <TextField
                      label="距离"
                      value={String(num(rec.distance))}
                      disabled={disabled}
                      onChange={(v) =>
                        onChangeRoot(
                          setAtPath(
                            root,
                            childPath(itemPath, "distance"),
                            Number(v) || 0,
                          ),
                        )
                      }
                    />
                    <TextField
                      label="简介"
                      value={str(rec.bio)}
                      disabled={disabled}
                      multiline
                      onChange={(v) =>
                        onChangeRoot(
                          setAtPath(root, childPath(itemPath, "bio"), v),
                        )
                      }
                    />
                    <StringListField
                      label="标签"
                      value={asArray(rec.tags).map((t) => str(t))}
                      disabled={disabled}
                      onChange={(v) =>
                        onChangeRoot(
                          setAtPath(root, childPath(itemPath, "tags"), v),
                        )
                      }
                    />
                    <BoolField
                      label="已认证"
                      value={bool(rec.verified)}
                      disabled={disabled}
                      onChange={(v) =>
                        onChangeRoot(
                          setAtPath(root, childPath(itemPath, "verified"), v),
                        )
                      }
                    />
                    <MediaUrlField
                      label="图片 URL"
                      value={str(rec.image_url)}
                      disabled={disabled}
                      fieldKey="image_url"
                      onChange={(v) =>
                        onChangeRoot(
                          setAtPath(root, childPath(itemPath, "image_url"), v),
                        )
                      }
                    />
                  </>
                ) : (
                  <>
                    <TextField
                      label="资料名"
                      value={str(rec.profile)}
                      disabled={disabled}
                      onChange={(v) =>
                        onChangeRoot(
                          setAtPath(root, childPath(itemPath, "profile"), v),
                        )
                      }
                    />
                    <TextField
                      label="最后消息"
                      value={str(rec.message)}
                      disabled={disabled}
                      multiline
                      onChange={(v) =>
                        onChangeRoot(
                          setAtPath(root, childPath(itemPath, "message"), v),
                        )
                      }
                    />
                    <BoolField
                      label="未读"
                      value={bool(rec.unread)}
                      disabled={disabled}
                      onChange={(v) =>
                        onChangeRoot(
                          setAtPath(root, childPath(itemPath, "unread"), v),
                        )
                      }
                    />
                    <TextField
                      label="最后消息时间"
                      value={str(rec.last_message_at)}
                      disabled={disabled}
                      placeholder="YYYY-MM-DDTHH:mm:ss"
                      onChange={(v) =>
                        onChangeRoot(
                          setAtPath(
                            root,
                            childPath(itemPath, "last_message_at"),
                            v,
                          ),
                        )
                      }
                    />
                  </>
                )}
              </RowCard>
            );
          })}
        </ul>
      </ArrayShell>
    </div>
  );
}

/** Reminders: lists with nested items */
export function RemindersPanel({ root, disabled, onChangeRoot }: PanelProps) {
  const path = "device.apps.reminders";
  const lists = asArray(getAtPath(root, path));
  const { openIndex, setOpenIndex, toggle } = useOpenIndex(lists.length);
  const itemSchema = getSchemaAt(CASE_TYPE_SCHEMA, "device.apps.reminders[0]");

  return (
    <ArrayShell
      title="提醒事项"
      count={lists.length}
      disabled={disabled}
      onAdd={() => {
        const row = itemSchema
          ? emptyValueForSchema(itemSchema)
          : { name: "", items: [] };
        onChangeRoot(appendAtPath(root, path, row));
        setOpenIndex(lists.length);
      }}
    >
      <ul className="flex flex-col gap-2">
        {lists.map((list, index) => {
          const rec = isRecord(list) ? list : {};
          const itemPath = childPath(path, index);
          const itemsPath = childPath(itemPath, "items");
          const items = asArray(rec.items);
          return (
            <RowCard
              key={itemPath}
              title={str(rec.name) || `列表 ${index + 1}`}
              open={openIndex === index}
              onToggle={() => toggle(index)}
              disabled={disabled}
              index={index}
              total={lists.length}
              arrayPath={path}
              root={root}
              onChangeRoot={onChangeRoot}
            >
              <TextField
                label="列表名"
                value={str(rec.name)}
                disabled={disabled}
                onChange={(v) =>
                  onChangeRoot(
                    setAtPath(root, childPath(itemPath, "name"), v),
                  )
                }
              />
              <TextField
                label="颜色"
                value={str(rec.color)}
                disabled={disabled}
                onChange={(v) =>
                  onChangeRoot(
                    setAtPath(root, childPath(itemPath, "color"), v),
                  )
                }
              />
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-medium">条目 ({items.length})</p>
                  <Button
                    type="button"
                    size="xs"
                    variant="outline"
                    disabled={disabled}
                    onClick={() =>
                      onChangeRoot(
                        appendAtPath(root, itemsPath, {
                          title: "",
                          done: false,
                        }),
                      )
                    }
                  >
                    添加条目
                  </Button>
                </div>
                {items.map((it, j) => {
                  const ir = isRecord(it) ? it : {};
                  const iPath = childPath(itemsPath, j);
                  return (
                    <div
                      key={j}
                      className="flex flex-col gap-2 rounded-md border border-border/50 p-2"
                    >
                      <TextField
                        label="标题"
                        value={str(ir.title)}
                        disabled={disabled}
                        onChange={(v) =>
                          onChangeRoot(
                            setAtPath(root, childPath(iPath, "title"), v),
                          )
                        }
                      />
                      <BoolField
                        label="完成"
                        value={bool(ir.done)}
                        disabled={disabled}
                        onChange={(v) =>
                          onChangeRoot(
                            setAtPath(root, childPath(iPath, "done"), v),
                          )
                        }
                      />
                    </div>
                  );
                })}
              </div>
            </RowCard>
          );
        })}
      </ul>
    </ArrayShell>
  );
}

const SIRI_ICON_LABELS = {
  home: "家",
  car: "汽车",
  place: "地点",
};

/** Maps: map_url + Siri suggestions + visits */
export function MapsPanel({ root, disabled, onChangeRoot }: PanelProps) {
  const suggestionsPath = "device.apps.maps.siri_suggestions";
  const path = "device.apps.maps.visits";
  const suggestions = asArray(getAtPath(root, suggestionsPath));
  const visits = asArray(getAtPath(root, path));
  const {
    openIndex: openSuggestionIndex,
    setOpenIndex: setOpenSuggestionIndex,
    toggle: toggleSuggestion,
  } = useOpenIndex(suggestions.length);
  const { openIndex, setOpenIndex, toggle } = useOpenIndex(visits.length);
  const suggestionSchema = getSchemaAt(
    CASE_TYPE_SCHEMA,
    `${suggestionsPath}[0]`,
  );
  const itemSchema = getSchemaAt(CASE_TYPE_SCHEMA, "device.apps.maps.visits[0]");

  return (
    <div className="flex flex-col gap-3">
      <MediaUrlField
        label="地图图"
        value={str(getAtPath(root, "device.apps.maps.map_url"))}
        disabled={disabled}
        fieldKey="map_url"
        onChange={(v) =>
          onChangeRoot(setAtPath(root, "device.apps.maps.map_url", v))
        }
      />
      <ArrayShell
        title="Siri 建议"
        count={suggestions.length}
        disabled={disabled}
        emptyText="尚未配置，手机端不会显示 Siri 建议。"
        onAdd={() => {
          const row = suggestionSchema
            ? emptyValueForSchema(suggestionSchema)
            : { icon: "home", title: "", subtitle: "" };
          onChangeRoot(appendAtPath(root, suggestionsPath, row));
          setOpenSuggestionIndex(suggestions.length);
        }}
      >
        <ul className="flex flex-col gap-2">
          {suggestions.map((suggestion, index) => {
            const rec = isRecord(suggestion) ? suggestion : {};
            const itemPath = childPath(suggestionsPath, index);
            const title = str(rec.title) || `建议 ${index + 1}`;
            return (
              <RowCard
                key={itemPath}
                title={title}
                open={openSuggestionIndex === index}
                onToggle={() => toggleSuggestion(index)}
                disabled={disabled}
                index={index}
                total={suggestions.length}
                arrayPath={suggestionsPath}
                root={root}
                onChangeRoot={onChangeRoot}
              >
                <EnumSelect
                  label="图标"
                  value={str(rec.icon, "home")}
                  options={enumOptionsAt(`${itemPath}.icon`)}
                  optionLabels={SIRI_ICON_LABELS}
                  disabled={disabled}
                  onChange={(value) =>
                    onChangeRoot(
                      setAtPath(root, childPath(itemPath, "icon"), value),
                    )
                  }
                />
                <TextField
                  label="标题"
                  value={str(rec.title)}
                  disabled={disabled}
                  onChange={(value) =>
                    onChangeRoot(
                      setAtPath(root, childPath(itemPath, "title"), value),
                    )
                  }
                />
                <TextField
                  label="副标题"
                  value={str(rec.subtitle)}
                  disabled={disabled}
                  onChange={(value) =>
                    onChangeRoot(
                      setAtPath(root, childPath(itemPath, "subtitle"), value),
                    )
                  }
                />
              </RowCard>
            );
          })}
        </ul>
      </ArrayShell>
      <ArrayShell
        title="访问记录"
        count={visits.length}
        disabled={disabled}
        onAdd={() => {
          const row = itemSchema
            ? emptyValueForSchema(itemSchema)
            : { visited_at: "", duration_min: 0 };
          onChangeRoot(appendAtPath(root, path, row));
          setOpenIndex(visits.length);
        }}
      >
        <ul className="flex flex-col gap-2">
          {visits.map((visit, index) => {
            const rec = isRecord(visit) ? visit : {};
            const itemPath = childPath(path, index);
            const title =
              str(rec.location) ||
              str(rec.poi) ||
              str(rec.visited_at) ||
              `访问 ${index + 1}`;
            return (
              <RowCard
                key={itemPath}
                title={title}
                open={openIndex === index}
                onToggle={() => toggle(index)}
                disabled={disabled}
                index={index}
                total={visits.length}
                arrayPath={path}
                root={root}
                onChangeRoot={onChangeRoot}
              >
                <TextField
                  label="访问时间"
                  fieldKey="visited_at"
                  value={str(rec.visited_at)}
                  disabled={disabled}
                  placeholder="YYYY-MM-DDTHH:mm:ss"
                  onChange={(v) =>
                    onChangeRoot(
                      setAtPath(root, childPath(itemPath, "visited_at"), v),
                    )
                  }
                />
                <TextField
                  label="地点"
                  value={str(rec.location)}
                  disabled={disabled}
                  onChange={(v) =>
                    onChangeRoot(
                      setAtPath(root, childPath(itemPath, "location"), v),
                    )
                  }
                />
                <TextField
                  label="兴趣点"
                  value={str(rec.poi)}
                  disabled={disabled}
                  onChange={(v) =>
                    onChangeRoot(setAtPath(root, childPath(itemPath, "poi"), v))
                  }
                />
                <TextField
                  label="停留分钟"
                  value={
                    typeof rec.duration_min === "number"
                      ? String(rec.duration_min)
                      : str(rec.duration_min)
                  }
                  disabled={disabled}
                  onChange={(v) => {
                    const n = Number(v);
                    onChangeRoot(
                      setAtPath(
                        root,
                        childPath(itemPath, "duration_min"),
                        Number.isFinite(n) ? n : v,
                      ),
                    );
                  }}
                />
              </RowCard>
            );
          })}
        </ul>
      </ArrayShell>
    </div>
  );
}

/** Airbnb: trips + map_url */
export function AirbnbPanel({ root, disabled, onChangeRoot }: PanelProps) {
  const path = "device.apps.airbnb.trips";
  const trips = asArray(getAtPath(root, path));
  const { openIndex, setOpenIndex, toggle } = useOpenIndex(trips.length);
  const itemSchema = getSchemaAt(CASE_TYPE_SCHEMA, "device.apps.airbnb.trips[0]");

  return (
    <div className="flex flex-col gap-3">
      <MediaUrlField
        label="地图 URL"
        value={str(getAtPath(root, "device.apps.airbnb.map_url"))}
        disabled={disabled}
        fieldKey="map_url"
        onChange={(v) =>
          onChangeRoot(setAtPath(root, "device.apps.airbnb.map_url", v))
        }
      />
      <ArrayShell
        title="行程"
        count={trips.length}
        disabled={disabled}
        onAdd={() => {
          const row = itemSchema
            ? emptyValueForSchema(itemSchema)
            : { check_in_at: "", check_out_at: "" };
          onChangeRoot(appendAtPath(root, path, row));
          setOpenIndex(trips.length);
        }}
      >
        <ul className="flex flex-col gap-2">
          {trips.map((trip, index) => {
            const rec = isRecord(trip) ? trip : {};
            const itemPath = childPath(path, index);
            const title =
              str(rec.place) ||
              str(rec.location) ||
              str(rec.check_in_at) ||
              `行程 ${index + 1}`;
            return (
              <RowCard
                key={itemPath}
                title={title}
                open={openIndex === index}
                onToggle={() => toggle(index)}
                disabled={disabled}
                index={index}
                total={trips.length}
                arrayPath={path}
                root={root}
                onChangeRoot={onChangeRoot}
              >
                <TextField
                  label="入住"
                  fieldKey="check_in_at"
                  value={str(rec.check_in_at)}
                  disabled={disabled}
                  placeholder="YYYY-MM-DDTHH:mm:ss"
                  onChange={(v) =>
                    onChangeRoot(
                      setAtPath(root, childPath(itemPath, "check_in_at"), v),
                    )
                  }
                />
                <TextField
                  label="退房"
                  value={str(rec.check_out_at)}
                  disabled={disabled}
                  placeholder="YYYY-MM-DDTHH:mm:ss"
                  onChange={(v) =>
                    onChangeRoot(
                      setAtPath(root, childPath(itemPath, "check_out_at"), v),
                    )
                  }
                />
                <TextField
                  label="地点"
                  value={str(rec.location)}
                  disabled={disabled}
                  onChange={(v) =>
                    onChangeRoot(
                      setAtPath(root, childPath(itemPath, "location"), v),
                    )
                  }
                />
                <TextField
                  label="房源"
                  value={str(rec.place)}
                  disabled={disabled}
                  onChange={(v) =>
                    onChangeRoot(
                      setAtPath(root, childPath(itemPath, "place"), v),
                    )
                  }
                />
                <TextField
                  label="房东"
                  value={str(rec.host_name)}
                  disabled={disabled}
                  onChange={(v) =>
                    onChangeRoot(
                      setAtPath(root, childPath(itemPath, "host_name"), v),
                    )
                  }
                />
                <MediaUrlField
                  label="图片 URL"
                  value={str(rec.image_url)}
                  disabled={disabled}
                  fieldKey="image_url"
                  onChange={(v) =>
                    onChangeRoot(
                      setAtPath(root, childPath(itemPath, "image_url"), v),
                    )
                  }
                />
              </RowCard>
            );
          })}
        </ul>
      </ArrayShell>
    </div>
  );
}

/** PayPal: balance + orders */
export function PaypalPanel({ root, disabled, onChangeRoot }: PanelProps) {
  const path = "device.apps.paypal.orders";
  const orders = asArray(getAtPath(root, path));
  const { openIndex, setOpenIndex, toggle } = useOpenIndex(orders.length);
  const itemSchema = getSchemaAt(CASE_TYPE_SCHEMA, "device.apps.paypal.orders[0]");

  return (
    <div className="flex flex-col gap-3">
      <TextField
        label="余额"
        value={String(num(getAtPath(root, "device.apps.paypal.balance")))}
        disabled={disabled}
        onChange={(v) =>
          onChangeRoot(
            setAtPath(root, "device.apps.paypal.balance", Number(v) || 0),
          )
        }
      />
      <ArrayShell
        title="订单"
        count={orders.length}
        disabled={disabled}
        onAdd={() => {
          const row = itemSchema ? emptyValueForSchema(itemSchema) : {};
          onChangeRoot(appendAtPath(root, path, row));
          setOpenIndex(orders.length);
        }}
      >
        <ul className="flex flex-col gap-2">
          {orders.map((order, index) => {
            const rec = isRecord(order) ? order : {};
            const itemPath = childPath(path, index);
            const direction = rec.direction === "incoming" ? "incoming" : "outgoing";
            // generic field dump for paypal orders - use known keys if present
            const title =
              str(rec.title) ||
              str(rec.merchant) ||
              str(rec.id) ||
              `订单 ${index + 1}`;
            return (
              <RowCard
                key={itemPath}
                title={title}
                open={openIndex === index}
                onToggle={() => toggle(index)}
                disabled={disabled}
                index={index}
                total={orders.length}
                arrayPath={path}
                root={root}
                onChangeRoot={onChangeRoot}
              >
                <EnumSelect
                  label="方向"
                  fieldKey="direction"
                  value={direction}
                  options={enumOptionsAt("device.apps.paypal.orders[0].direction")}
                  optionLabels={{ incoming: "进账", outgoing: "出账" }}
                  disabled={disabled}
                  onChange={(v) =>
                    onChangeRoot(
                      setAtPath(root, childPath(itemPath, "direction"), v),
                    )
                  }
                />
                {Object.keys(rec).length === 0 ? (
                  <p className="text-xs text-muted-foreground">空订单对象</p>
                ) : (
                  Object.entries(rec).map(([key, value]) => {
                    if (key === "direction") return null;
                    if (typeof value === "boolean") {
                      return (
                        <BoolField
                          key={key}
                          fieldKey={key}
                          label={key}
                          value={value}
                          disabled={disabled}
                          onChange={(v) =>
                            onChangeRoot(
                              setAtPath(root, childPath(itemPath, key), v),
                            )
                          }
                        />
                      );
                    }
                    if (typeof value === "number") {
                      return (
                        <TextField
                          key={key}
                          fieldKey={key}
                          label={key}
                          value={String(value)}
                          disabled={disabled}
                          onChange={(v) =>
                            onChangeRoot(
                              setAtPath(
                                root,
                                childPath(itemPath, key),
                                Number(v) || 0,
                              ),
                            )
                          }
                        />
                      );
                    }
                    if (typeof value === "string") {
                      return (
                        <TextField
                          key={key}
                          fieldKey={key}
                          label={key}
                          value={value}
                          disabled={disabled}
                          multiline={value.length > 60}
                          onChange={(v) =>
                            onChangeRoot(
                              setAtPath(root, childPath(itemPath, key), v),
                            )
                          }
                        />
                      );
                    }
                    return null;
                  })
                )}
              </RowCard>
            );
          })}
        </ul>
      </ArrayShell>
    </div>
  );
}
